import { c as _c } from "react/compiler-runtime";
import CommonAnimationHeight from "@/components/utils/animation/CommonAnimationHeight";
import CommonBorderedRadioButton from "@/components/utils/radio/CommonBorderedRadioButton";
import { ApiWaveParticipationIdentitySubmissionAllowDuplicates } from "@/generated/models/ApiWaveParticipationIdentitySubmissionAllowDuplicates";
import { ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted } from "@/generated/models/ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted";
import type { ApiWaveParticipationSubmissionStrategy } from "@/generated/models/ApiWaveParticipationSubmissionStrategy";
import { ApiWaveParticipationSubmissionStrategyType } from "@/generated/models/ApiWaveParticipationSubmissionStrategyType";
import { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { WAVE_IDENTITY_DUPLICATES_COPY, WAVE_IDENTITY_WHO_CAN_BE_SUBMITTED_COPY } from "@/helpers/waves/wave-submission-strategy.helpers";
enum CreateWaveSubmissionMode {
  STANDARD = "STANDARD",
  IDENTITY = "IDENTITY",
}
const createDefaultIdentitySubmissionStrategy = (): ApiWaveParticipationSubmissionStrategy => ({
  type: ApiWaveParticipationSubmissionStrategyType.Identity,
  config: {
    duplicates: ApiWaveParticipationIdentitySubmissionAllowDuplicates.NeverAllow,
    who_can_be_submitted: ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted.Everyone
  }
});
type SubmissionOptionSection = "who-can-be-submitted" | "duplicate-submissions";
type SubmissionOptionRowProps<T extends string> = {
  readonly type: T;
  readonly selected: T;
  readonly label: string;
  readonly description: string;
  readonly titleId: string;
  readonly descriptionId: string;
  readonly groupName: string;
  readonly onChange: (type: T) => void;
};
const getOptionDescriptionId = (section: SubmissionOptionSection, option: string) => `${section}-${option.toLowerCase()}-description`;
const getOptionTitleId = (section: SubmissionOptionSection, option: string) => `${section}-${option.toLowerCase()}-title`;
function SubmissionOptionRow(t0) {
  const $ = _c(25);
  const {
    type,
    selected,
    label,
    description,
    titleId,
    descriptionId,
    groupName,
    onChange
  } = t0;
  const isSelected = selected === type;
  const wrapperClasses = isSelected ? "tw-ring-primary-400 tw-bg-[#202B45] tw-shadow-xl" : "tw-ring-iron-700 tw-bg-iron-800 tw-shadow hover:tw-ring-iron-650";
  const inputClasses = isSelected ? "tw-text-primary-500 focus:tw-ring-primary-500" : "tw-text-primary-400 focus:tw-ring-primary-400";
  const labelClasses = isSelected ? "tw-text-primary-400" : "tw-text-iron-300 group-hover:tw-text-iron-200";
  const descriptionClasses = isSelected ? "tw-text-iron-200" : "tw-text-iron-400";
  const t1 = `${wrapperClasses} tw-group tw-flex tw-w-full tw-cursor-pointer tw-gap-x-4 tw-rounded-lg tw-px-4 tw-py-4 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out`;
  let t2;
  if ($[0] !== onChange || $[1] !== type) {
    t2 = () => onChange(type);
    $[0] = onChange;
    $[1] = type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `${inputClasses} tw-form-radio tw-mt-1 tw-h-4 tw-w-4 tw-flex-shrink-0 tw-cursor-pointer tw-border tw-border-solid tw-border-iron-650 tw-bg-iron-800 tw-ring-offset-iron-800 tw-transition tw-duration-300 tw-ease-out focus:tw-ring-2 sm:tw-h-5 sm:tw-w-5`;
  let t4;
  if ($[3] !== descriptionId || $[4] !== groupName || $[5] !== isSelected || $[6] !== t2 || $[7] !== t3 || $[8] !== titleId) {
    t4 = <input type="radio" name={groupName} checked={isSelected} onChange={t2} aria-labelledby={titleId} aria-describedby={descriptionId} className={t3} />;
    $[3] = descriptionId;
    $[4] = groupName;
    $[5] = isSelected;
    $[6] = t2;
    $[7] = t3;
    $[8] = titleId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `${labelClasses} tw-text-base tw-font-semibold`;
  let t6;
  if ($[10] !== label || $[11] !== t5 || $[12] !== titleId) {
    t6 = <span id={titleId} className={t5}>{label}</span>;
    $[10] = label;
    $[11] = t5;
    $[12] = titleId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = `${descriptionClasses} tw-text-sm tw-font-medium tw-leading-6`;
  let t8;
  if ($[14] !== description || $[15] !== descriptionId || $[16] !== t7) {
    t8 = <span id={descriptionId} className={t7}>{description}</span>;
    $[14] = description;
    $[15] = descriptionId;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t6 || $[19] !== t8) {
    t9 = <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-y-2">{t6}{t8}</div>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t1 || $[22] !== t4 || $[23] !== t9) {
    t10 = <label className={t1}>{t4}{t9}</label>;
    $[21] = t1;
    $[22] = t4;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
export default function CreateWaveDropsSubmissionMode(t0) {
  const $ = _c(29);
  const {
    submissionStrategy,
    errors,
    onChange
  } = t0;
  const selectedMode = submissionStrategy === null ? CreateWaveSubmissionMode.STANDARD : CreateWaveSubmissionMode.IDENTITY;
  let t1;
  if ($[0] !== errors) {
    t1 = errors.includes(CREATE_WAVE_VALIDATION_ERROR.DROPS_SUBMISSION_STRATEGY_INVALID);
    $[0] = errors;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showSubmissionStrategyError = t1;
  let t2;
  if ($[2] !== onChange || $[3] !== submissionStrategy) {
    t2 = mode => {
      if (mode === CreateWaveSubmissionMode.STANDARD) {
        onChange(null);
        return;
      }
      onChange(submissionStrategy ?? createDefaultIdentitySubmissionStrategy());
    };
    $[2] = onChange;
    $[3] = submissionStrategy;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onSubmissionModeChange = t2;
  let t3;
  if ($[5] !== onChange || $[6] !== submissionStrategy) {
    t3 = whoCanBeSubmitted => {
      if (!submissionStrategy) {
        return;
      }
      onChange({
        ...submissionStrategy,
        config: {
          ...submissionStrategy.config,
          who_can_be_submitted: whoCanBeSubmitted
        }
      });
    };
    $[5] = onChange;
    $[6] = submissionStrategy;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const onWhoCanBeSubmittedChange = t3;
  let t4;
  if ($[8] !== onChange || $[9] !== submissionStrategy) {
    t4 = duplicates => {
      if (!submissionStrategy) {
        return;
      }
      onChange({
        ...submissionStrategy,
        config: {
          ...submissionStrategy.config,
          duplicates
        }
      });
    };
    $[8] = onChange;
    $[9] = submissionStrategy;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const onDuplicatesChange = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div><p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">Submission type</p><p className="tw-mb-0 tw-mt-2 tw-text-sm tw-font-medium tw-text-iron-400">Choose whether participants submit drops or nominate identities.</p></div>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== onSubmissionModeChange || $[13] !== selectedMode) {
    t6 = <div className="tw-grid tw-gap-4 md:tw-grid-cols-2"><CommonBorderedRadioButton type={CreateWaveSubmissionMode.STANDARD} selected={selectedMode} label="Standard drops" onChange={onSubmissionModeChange} /><CommonBorderedRadioButton type={CreateWaveSubmissionMode.IDENTITY} selected={selectedMode} label="Identity nominations" onChange={onSubmissionModeChange} /></div>;
    $[12] = onSubmissionModeChange;
    $[13] = selectedMode;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onDuplicatesChange || $[16] !== onWhoCanBeSubmittedChange || $[17] !== submissionStrategy) {
    t7 = submissionStrategy && <div className="tw-flex tw-flex-col tw-gap-y-6"><div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-100">Which identities can a participant submit?</p><div className="tw-mt-3 tw-flex tw-flex-col tw-gap-y-4">{(Object.values(ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted) as ApiWaveParticipationIdentitySubmissionWhoCanBeSubmitted[]).map(option => {
            const titleId = getOptionTitleId("who-can-be-submitted", option);
            const descriptionId = getOptionDescriptionId("who-can-be-submitted", option);
            return <SubmissionOptionRow key={option} type={option} selected={submissionStrategy.config.who_can_be_submitted} label={WAVE_IDENTITY_WHO_CAN_BE_SUBMITTED_COPY[option].label} description={WAVE_IDENTITY_WHO_CAN_BE_SUBMITTED_COPY[option].description} titleId={titleId} descriptionId={descriptionId} groupName="who-can-be-submitted" onChange={onWhoCanBeSubmittedChange} />;
          })}</div></div><div><p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-100">When can the same identity be submitted again?</p><div className="tw-mt-3 tw-flex tw-flex-col tw-gap-y-4">{(Object.values(ApiWaveParticipationIdentitySubmissionAllowDuplicates) as ApiWaveParticipationIdentitySubmissionAllowDuplicates[]).map(option_0 => {
            const titleId_0 = getOptionTitleId("duplicate-submissions", option_0);
            const descriptionId_0 = getOptionDescriptionId("duplicate-submissions", option_0);
            return <SubmissionOptionRow key={option_0} type={option_0} selected={submissionStrategy.config.duplicates} label={WAVE_IDENTITY_DUPLICATES_COPY[option_0].label} description={WAVE_IDENTITY_DUPLICATES_COPY[option_0].description} titleId={titleId_0} descriptionId={descriptionId_0} groupName="duplicate-submissions" onChange={onDuplicatesChange} />;
          })}</div></div></div>;
    $[15] = onDuplicatesChange;
    $[16] = onWhoCanBeSubmittedChange;
    $[17] = submissionStrategy;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <CommonAnimationHeight>{t7}</CommonAnimationHeight>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== showSubmissionStrategyError) {
    t9 = showSubmissionStrategyError && <div className="tw-text-sm tw-font-medium tw-text-error">Complete the identity nomination settings before continuing.</div>;
    $[21] = showSubmissionStrategyError;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t9) {
    t10 = <CommonAnimationHeight>{t9}</CommonAnimationHeight>;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t6 || $[27] !== t8) {
    t11 = <div className="tw-flex tw-flex-col tw-gap-y-6">{t5}{t6}{t8}{t10}</div>;
    $[25] = t10;
    $[26] = t6;
    $[27] = t8;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}